import type {
  ActionRequest,
  Decision,
  ResumeRequest,
  SSEEvent,
  StreamRequest,
} from "./types";
import type {
  TaskEditorBlock,
  TaskEditorState,
} from "../utils/taskEditorUtils";

interface MockSession {
  currentTask: TaskEditorState | null;
  pendingAction?: ActionRequest;
}

const sessions = new Map<string, MockSession>();

function getSession(threadId: string): MockSession {
  let s = sessions.get(threadId);
  if (!s) {
    s = { currentTask: null };
    sessions.set(threadId, s);
  }
  return s;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

const rid = () => Math.random().toString(36).slice(2, 10);

function ev<E extends SSEEvent["event"]>(
  event: E,
  data: Extract<SSEEvent, { event: E }>["data"]
): SSEEvent {
  return { event, data } as SSEEvent;
}

function chunkText(text: string): string[] {
  // 한글/영문 혼합 — 2~4 글자씩 자르되 공백 경계는 보존
  const chunks: string[] = [];
  let buf = "";
  for (const ch of text) {
    buf += ch;
    if (buf.length >= 2 + Math.floor(Math.random() * 3)) {
      chunks.push(buf);
      buf = "";
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

// =========================
// Mock guide content
// =========================

const MOCK_GUIDES_LISTING = `README.md
tone.md
structure.md
components/
  textarea.md
  button-group.md
  quiz.md
examples/
  day1-welcome.json
  day2-mood-check.json`;

const MOCK_README_PREVIEW = `# 활동지 제작 가이드

활동지는 사용자가 단계별로 따라가는 학습 자료입니다.
각 활동지는 blocks 배열로 구성되며, 마지막 block 은 반드시 LastCard 입니다.

블록 종류:
- Header1: 큰 제목
- Instruction: 본문 (lines 배열 포함)
- LastCard: 마지막 카드 (고정)

자세한 컴포넌트 사용법은 components/ 참고.`;

const MOCK_TONE_PREVIEW = `# 톤 가이드

- 반말 X, 존댓말 사용
- "~합니다" 보다 "~해요" 선호
- 의학 용어 최소화, 일상어 우선
- 1~2문장으로 짧게 끊어 쓰기
- 사용자를 다그치지 않기`;

const MOCK_COMPONENTS_TEXTAREA = `# Textarea Line

사용자가 자유롭게 입력하는 영역입니다.

\`\`\`
{
  type: "Textarea",
  content: "current_mood",         // snake_case 데이터 바인딩 ID
  textareaPlaceholder: "오늘 기분을 적어주세요"
}
\`\`\``;

// =========================
// Mock task builder
// =========================

function buildMockNewTask(): TaskEditorState {
  const blocks: TaskEditorBlock[] = [
    {
      id: "block-header-" + rid(),
      type: "Header1",
      content: "디스턴싱 시작하기",
    },
    {
      id: "block-intro-" + rid(),
      type: "Instruction",
      content: "",
      lines: [
        {
          id: "line-" + rid(),
          type: "Typography",
          content: "안녕하세요! 디스턴싱에 오신 것을 환영해요.",
          typographyStyle: "Body",
        },
        {
          id: "line-" + rid(),
          type: "Typography",
          content: "오늘은 첫 만남이라 가볍게 시작해볼게요.",
          typographyStyle: "Body",
        },
      ],
    },
    {
      id: "block-mood-" + rid(),
      type: "Instruction",
      content: "",
      lines: [
        {
          id: "line-" + rid(),
          type: "Typography",
          content: "지금 기분이 어떠신가요?",
          typographyStyle: "Label",
        },
        {
          id: "line-" + rid(),
          type: "Textarea",
          content: "current_mood",
          textareaPlaceholder: "솔직하게 적어주세요",
        },
      ],
    },
    {
      id: "block-closing-" + rid(),
      type: "Instruction",
      content: "",
      lines: [
        {
          id: "line-" + rid(),
          type: "Typography",
          content: "잘 하셨어요. 다음 활동지에서 만나요.",
          typographyStyle: "Body",
        },
      ],
    },
    {
      id: "task-editor-last-card",
      type: "LastCard",
      content: "common:lastCard1\ncommon:lastCard2",
    },
  ];

  return {
    taskId: "mock-day1-welcome",
    title: "디스턴싱 시작하기",
    setNumber: 1,
    isKeyActivity: false,
    blocks,
  };
}

// =========================
// Apply mock edit_file/write_file
// =========================

function applyMockEdit(
  task: TaskEditorState | null,
  action: { name: string; args: Record<string, unknown> }
): TaskEditorState | null {
  if (action.name === "write_file") {
    const content = action.args.content;
    if (typeof content === "string") {
      try {
        return JSON.parse(content) as TaskEditorState;
      } catch {
        return task;
      }
    }
    return task;
  }
  if (action.name === "edit_file" && task) {
    const oldStr = String(action.args.old_string ?? "");
    const newStr = String(action.args.new_string ?? "");
    const json = JSON.stringify(task);
    const replaced = json.split(oldStr).join(newStr);
    try {
      return JSON.parse(replaced) as TaskEditorState;
    } catch {
      return task;
    }
  }
  return task;
}

// =========================
// Scenario routing
// =========================

type Scenario = "greet" | "create" | "title" | "addBlock" | "generic";

function pickScenario(
  message: string,
  currentTask: TaskEditorState | null
): Scenario {
  const m = message.toLowerCase();
  if (/(^|\s)(안녕|반가|hi|hello)/.test(m)) return "greet";
  if (!currentTask && /(만들|생성|create|new|시작)/.test(m)) return "create";
  if (/(만들|생성|create|new) (활동지|task)/.test(m)) return "create";
  if (/(제목|타이틀|title)/.test(m)) return "title";
  if (/(추가|add|블록|block)/.test(m)) return "addBlock";
  return "generic";
}

// =========================
// Stream entry
// =========================

export async function* mockStreamTaskAuthoring(
  req: StreamRequest,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const tid = req.thread_id;
  const session = getSession(tid);
  if (req.current_task !== undefined && req.current_task !== null) {
    session.currentTask = req.current_task;
  }

  const scenario = pickScenario(req.message, session.currentTask);

  yield ev("session", { thread_id: tid });
  await sleep(200, signal);

  switch (scenario) {
    case "greet":
      yield* greetScenario(tid, signal);
      return;
    case "create":
      yield* createScenario(tid, signal);
      return;
    case "title":
      yield* titleScenario(tid, signal);
      return;
    case "addBlock":
      yield* addBlockScenario(tid, signal);
      return;
    default:
      yield* genericScenario(tid, signal);
      return;
  }
}

async function* greetScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  for (const c of chunkText(
    "안녕하세요! 활동지를 함께 만들어볼까요? 어떤 주제가 좋을까요?"
  )) {
    if (signal?.aborted) return;
    yield ev("message_chunk", { delta: c });
    await sleep(40, signal);
  }
  yield ev("final", {
    thread_id: tid,
    assistant_message:
      "안녕하세요! 활동지를 함께 만들어볼까요? 어떤 주제가 좋을까요?",
    task: null,
    task_changed: false,
  });
  yield ev("debug", {
    thread_id: tid,
    inference_id: "mock-inf-" + rid(),
    trace_id: null,
    execution_time_ms: 800,
  });
}

async function* createScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield ev("tool_call", { name: "ls", args: { path: "/guides" } });
  await sleep(450, signal);
  yield ev("tool_result", { name: "ls", preview: MOCK_GUIDES_LISTING });

  yield ev("tool_call", {
    name: "read_file",
    args: { file_path: "/guides/README.md" },
  });
  await sleep(600, signal);
  yield ev("tool_result", {
    name: "read_file",
    preview: MOCK_README_PREVIEW,
  });

  yield ev("tool_call", {
    name: "read_file",
    args: { file_path: "/guides/tone.md" },
  });
  await sleep(500, signal);
  yield ev("tool_result", { name: "read_file", preview: MOCK_TONE_PREVIEW });

  yield ev("tool_call", {
    name: "read_file",
    args: { file_path: "/guides/components/textarea.md" },
  });
  await sleep(450, signal);
  yield ev("tool_result", {
    name: "read_file",
    preview: MOCK_COMPONENTS_TEXTAREA,
  });

  const reply =
    "가이드를 확인했어요. 첫 만남 인사 활동지를 만들어볼게요. " +
    "Header → 환영 인사 → 기분 입력 → 마무리 흐름으로 구성했어요.";
  for (const c of chunkText(reply)) {
    if (signal?.aborted) return;
    yield ev("message_chunk", { delta: c });
    await sleep(35, signal);
  }

  const draftTask = buildMockNewTask();
  session.currentTask = draftTask;
  const action: ActionRequest = {
    name: "write_file",
    args: {
      file_path: "/task.json",
      content: JSON.stringify(draftTask, null, 2),
    },
    description: "새 활동지 초안을 저장합니다 (block 5개, key activity 아님).",
  };
  session.pendingAction = action;

  yield ev("pending_approval", {
    thread_id: tid,
    interrupt_id: "mock-int-" + rid(),
    action_requests: [action],
    review_configs: [
      {
        action_name: "write_file",
        allowed_decisions: ["approve", "edit", "reject"],
      },
    ],
  });
  yield ev("debug", {
    thread_id: tid,
    inference_id: "mock-inf-" + rid(),
    trace_id: null,
    execution_time_ms: Date.now() - t0,
  });
}

async function* titleScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();
  const task = session.currentTask;

  yield ev("tool_call", {
    name: "read_file",
    args: { file_path: "/task.json" },
  });
  await sleep(400, signal);
  yield ev("tool_result", {
    name: "read_file",
    preview: task
      ? JSON.stringify(task, null, 2).slice(0, 400) + "\n..."
      : "(empty)",
  });

  for (const c of chunkText(
    "제목을 수정해드릴게요. 변경 내용을 확인해주세요."
  )) {
    if (signal?.aborted) return;
    yield ev("message_chunk", { delta: c });
    await sleep(35, signal);
  }

  if (!task) {
    yield ev("final", {
      thread_id: tid,
      assistant_message: "활동지가 아직 없어요. 먼저 활동지를 만들어볼까요?",
      task: null,
      task_changed: false,
    });
    yield ev("debug", {
      thread_id: tid,
      inference_id: "mock-inf-" + rid(),
      trace_id: null,
      execution_time_ms: Date.now() - t0,
    });
    return;
  }

  const oldTitle = task.title;
  const newTitle = oldTitle + " (수정됨)";
  const action: ActionRequest = {
    name: "edit_file",
    args: {
      file_path: "/task.json",
      old_string: `"title": "${oldTitle}"`,
      new_string: `"title": "${newTitle}"`,
    },
    description: `제목을 "${oldTitle}" → "${newTitle}" 으로 변경합니다.`,
  };
  session.pendingAction = action;

  yield ev("pending_approval", {
    thread_id: tid,
    interrupt_id: "mock-int-" + rid(),
    action_requests: [action],
    review_configs: [
      {
        action_name: "edit_file",
        allowed_decisions: ["approve", "edit", "reject"],
      },
    ],
  });
  yield ev("debug", {
    thread_id: tid,
    inference_id: "mock-inf-" + rid(),
    trace_id: null,
    execution_time_ms: Date.now() - t0,
  });
}

async function* addBlockScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();
  const task = session.currentTask;

  yield ev("tool_call", {
    name: "read_file",
    args: { file_path: "/guides/structure.md" },
  });
  await sleep(400, signal);
  yield ev("tool_result", {
    name: "read_file",
    preview:
      "# 활동지 구조\n\nblock 추가 시 LastCard 직전에 삽입.\n각 block id 는 유일해야 함.",
  });

  for (const c of chunkText("새 블록 하나 추가할게요. LastCard 직전에 삽입.")) {
    if (signal?.aborted) return;
    yield ev("message_chunk", { delta: c });
    await sleep(35, signal);
  }

  if (!task) {
    yield ev("final", {
      thread_id: tid,
      assistant_message: "활동지가 비어있어요. 먼저 만들어볼까요?",
      task: null,
      task_changed: false,
    });
    yield ev("debug", {
      thread_id: tid,
      inference_id: "mock-inf-" + rid(),
      trace_id: null,
      execution_time_ms: Date.now() - t0,
    });
    return;
  }

  // LastCard 직전에 새 Instruction block 삽입
  const lastCardIdx = task.blocks.findIndex((b) => b.type === "LastCard");
  const insertIdx = lastCardIdx >= 0 ? lastCardIdx : task.blocks.length;
  const newBlock: TaskEditorBlock = {
    id: "block-" + rid(),
    type: "Instruction",
    content: "",
    lines: [
      {
        id: "line-" + rid(),
        type: "Typography",
        content: "(새 블록) 여기에 내용을 적어보세요.",
        typographyStyle: "Body",
      },
    ],
  };
  const nextTask: TaskEditorState = {
    ...task,
    blocks: [
      ...task.blocks.slice(0, insertIdx),
      newBlock,
      ...task.blocks.slice(insertIdx),
    ],
  };

  const action: ActionRequest = {
    name: "write_file",
    args: {
      file_path: "/task.json",
      content: JSON.stringify(nextTask, null, 2),
    },
    description: `LastCard 직전에 새 Instruction 블록을 추가합니다.`,
  };
  session.pendingAction = action;
  session.currentTask = nextTask;

  yield ev("pending_approval", {
    thread_id: tid,
    interrupt_id: "mock-int-" + rid(),
    action_requests: [action],
    review_configs: [
      {
        action_name: "write_file",
        allowed_decisions: ["approve", "edit", "reject"],
      },
    ],
  });
  yield ev("debug", {
    thread_id: tid,
    inference_id: "mock-inf-" + rid(),
    trace_id: null,
    execution_time_ms: Date.now() - t0,
  });
}

async function* genericScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const t0 = Date.now();
  yield ev("tool_call", {
    name: "read_file",
    args: { file_path: "/guides/README.md" },
  });
  await sleep(500, signal);
  yield ev("tool_result", {
    name: "read_file",
    preview: MOCK_README_PREVIEW,
  });

  const reply =
    "구체적으로 어떤 작업을 원하시는지 알려주실 수 있나요? " +
    "예: '첫 활동지 만들어줘', '제목 바꿔줘', '블록 추가해줘'";
  for (const c of chunkText(reply)) {
    if (signal?.aborted) return;
    yield ev("message_chunk", { delta: c });
    await sleep(35, signal);
  }

  yield ev("final", {
    thread_id: tid,
    assistant_message: reply,
    task: null,
    task_changed: false,
  });
  yield ev("debug", {
    thread_id: tid,
    inference_id: "mock-inf-" + rid(),
    trace_id: null,
    execution_time_ms: Date.now() - t0,
  });
}

// =========================
// Resume entry
// =========================

export async function* mockResumeTaskAuthoring(
  req: ResumeRequest,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const tid = req.thread_id;
  const session = getSession(tid);
  const action = session.pendingAction;
  const decision: Decision | undefined = req.decisions[0];
  const t0 = Date.now();

  if (!action || !decision) {
    yield ev("error", {
      thread_id: tid,
      type: "no_pending_action",
      message: "No pending action to resume.",
    });
    yield ev("debug", {
      thread_id: tid,
      inference_id: "mock-inf-" + rid(),
      trace_id: null,
      execution_time_ms: Date.now() - t0,
    });
    return;
  }

  if (decision.type === "approve") {
    yield ev("tool_result", { name: action.name, preview: "OK" });
    await sleep(200, signal);
    const reply = "적용했어요. 미리보기에서 확인해보세요.";
    for (const c of chunkText(reply)) {
      if (signal?.aborted) return;
      yield ev("message_chunk", { delta: c });
      await sleep(35, signal);
    }
    if (session.currentTask) {
      yield ev("task_patch", {
        thread_id: tid,
        task: session.currentTask,
      });
    }
    yield ev("final", {
      thread_id: tid,
      assistant_message: reply,
      task: session.currentTask,
      task_changed: !!session.currentTask,
    });
  } else if (decision.type === "edit") {
    const updated = applyMockEdit(session.currentTask, decision.edited_action);
    session.currentTask = updated;
    yield ev("tool_result", {
      name: decision.edited_action.name,
      preview: "OK (edited)",
    });
    await sleep(200, signal);
    const reply = "수정하신 내용으로 적용했어요.";
    for (const c of chunkText(reply)) {
      if (signal?.aborted) return;
      yield ev("message_chunk", { delta: c });
      await sleep(35, signal);
    }
    if (updated) {
      yield ev("task_patch", { thread_id: tid, task: updated });
    }
    yield ev("final", {
      thread_id: tid,
      assistant_message: reply,
      task: updated,
      task_changed: !!updated,
    });
  } else {
    // reject
    const why = decision.message?.trim() || "다른 방식으로 해주세요";
    const reply = `알겠어요. "${why}" — 다음에 다시 시도해볼게요.`;
    for (const c of chunkText(reply)) {
      if (signal?.aborted) return;
      yield ev("message_chunk", { delta: c });
      await sleep(35, signal);
    }
    yield ev("final", {
      thread_id: tid,
      assistant_message: reply,
      task: session.currentTask,
      task_changed: false,
    });
  }

  yield ev("debug", {
    thread_id: tid,
    inference_id: "mock-inf-" + rid(),
    trace_id: null,
    execution_time_ms: Date.now() - t0,
  });

  session.pendingAction = undefined;
}
